import { Box, Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, TextField, MenuItem } from "@mui/material";
import { useContext, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { StepperContext, StepperContextModel } from "../../../contexts/request-stepper-context";
import { useTranslation } from "react-i18next";
import jobApplicationService from "../../../services/job-application-service";
import { useNavigate } from "react-router-dom";
import { visaTypes } from "../../../models/shared-data";

export interface DSFormRequest {
    haveDirectRelativeInUS: boolean;
    relatives: string;
    deniedVisa: boolean;
    howLongWasVisaDenied: number;
    canceledVisa: boolean;
    whenWasVisaCanceled: Date;
    citizenRequest: boolean;
    currentlyWithVisaProcess: boolean;
    whatKindOfVisaIsProcessing: string;
    outOfCountryLastFiveYears: boolean;
    visitedCountries: string;
    haveBeenOut: boolean;
    triedEnterIllegallyToUs: boolean;
}

export default function DSForm() {
    const { t } = useTranslation();
    const { register, handleSubmit, formState: { errors } } = useForm<DSFormRequest>();
    const { handleBack, setDSForm, personalInfo, dsForm } = useContext(StepperContext) as StepperContextModel;
    const [haveDirectRelativeInUS, setHaveDirectRelativeInUS] = useState<boolean>(false);
    const [selectedRelative, setSelectedRelative] = useState<string>('');
    const [deniedVisa, setDeniedVisa] = useState<boolean>(false);
    const [canceledVisa, setCanceledVisa] = useState<boolean>(false);
    const [currentlyWithVisaProcess, setCurrentlyWithVisaProcess] = useState<boolean>(false);
    const [outOfTheCountryLastFiveYears, setOutOfTheCountryLastFiveYears] = useState<boolean>(false);
    const navigate = useNavigate();

    const handleForm: SubmitHandler<DSFormRequest> = (data) => {
        // Convert string values to proper boolean types
        const formattedDSForm = {
            ...data,
            relatives: selectedRelative,
            haveDirectRelativeInUS: Boolean(data.haveDirectRelativeInUS),
            deniedVisa: Boolean(data.deniedVisa),
            canceledVisa: Boolean(data.canceledVisa),
            citizenRequest: Boolean(data.citizenRequest),
            currentlyWithVisaProcess: Boolean(data.currentlyWithVisaProcess),
            outOfCountryLastFiveYears: Boolean(data.outOfCountryLastFiveYears),
            haveBeenOut: Boolean(data.haveBeenOut),
            triedEnterIllegallyToUs: Boolean(data.triedEnterIllegallyToUs),
            howLongWasVisaDenied: typeof data.howLongWasVisaDenied === 'string' ? parseInt(data.howLongWasVisaDenied) : data.howLongWasVisaDenied
        };
        
        console.log(formattedDSForm);
        jobApplicationService.createJobApplication({
            ...personalInfo,
            dSForm: formattedDSForm
        })
        .then(resp => {
            navigate("/confirmation");
            console.log(resp);
        })
        .catch((error) => {
            console.log(error);
        });
    }
    return (
        <Box>
            <form style={{
                marginTop: 20,
                marginBottom: 10
            }} onSubmit={handleSubmit(handleForm)}>

                <FormControl component="fieldset" sx={{ display: 'block', marginTop: 2, marginBottom: 2, width: '100%' }}>
                    <FormLabel component="legend">{t("forms.dsForm.haveDirectRelativeInUS")}</FormLabel>
                    <RadioGroup row {...register('haveDirectRelativeInUS', {
                        onChange: (event) => setHaveDirectRelativeInUS(event.target.value === 'true')
                    })} defaultValue={false}>
                        <FormControlLabel value={true} control={<Radio />} label={t('forms.yes')} />
                        <FormControlLabel value={false} control={<Radio />} label={t('forms.no')} />
                    </RadioGroup>
                </FormControl>

                {haveDirectRelativeInUS &&
                    <FormControl component="fieldset" sx={{ display: 'block', marginTop: 2, marginBottom: 2, width: '100%' }}>
                        <FormLabel component="legend">{t("forms.dsForm.relatives")}</FormLabel>
                        <RadioGroup 
                            {...register('relatives')}
                            value={selectedRelative}
                            onChange={(event) => {
                                setSelectedRelative(event.target.value);
                            }}
                        >
                            <FormControlLabel value="cousin" control={<Radio />} label={t('forms.dsForm.cousin')} />
                            <FormControlLabel value="brotherOrSister" control={<Radio />} label={t('forms.dsForm.brotherOrSister')} />
                            <FormControlLabel value="brotherInLaw" control={<Radio />} label={t('forms.dsForm.brotherInLaw')} />
                            <FormControlLabel value="husbandOrWife" control={<Radio />} label={t('forms.dsForm.husbandOrWife')} />
                            <FormControlLabel value="uncles" control={<Radio />} label={t('forms.dsForm.uncles')} />
                        </RadioGroup>
                        {!selectedRelative && (
                            <FormLabel error sx={{ fontSize: '0.75rem', mt: 1 }}>
                                 {t("forms.dsForm.relativesRequired") }
                            </FormLabel>
                        )}
                    </FormControl>}

                <FormControl component="fieldset" sx={{ display: 'block', marginTop: 2, marginBottom: 2, width: '100%' }}>
                    <FormLabel component="legend">{t("forms.dsForm.deniedVisa")}</FormLabel>
                    <RadioGroup row {...register('deniedVisa', {
                        onChange: (event) => setDeniedVisa(event.target.value === 'true')
                    })} defaultValue={false}>
                        <FormControlLabel value={true} control={<Radio />} label={t('forms.yes')} />
                        <FormControlLabel value={false} control={<Radio />} label={t('forms.no')} />
                    </RadioGroup>
                </FormControl>

                {deniedVisa && (
                    <TextField
                        id="howLongWasVisaDenied"
                        label={t('forms.dsForm.howLongWasVisaDenied')}
                        variant="outlined"
                        size="small"
                        select
                        defaultValue={dsForm?.howLongWasVisaDenied}
                        {...register('howLongWasVisaDenied', { 
                            required: deniedVisa ? t('forms.dsForm.howLongWasVisaDeniedRequired') : false
                        })}
                        error={errors!.howLongWasVisaDenied ? true : false}
                        helperText={errors.howLongWasVisaDenied?.message}
                        sx={{
                            width: { xs: '100%', sm: '48%' },
                            marginTop: 2,
                            marginBottom: 2
                        }}>
                        <MenuItem value={1}>{t('forms.dsForm.lessThanOneYear')}</MenuItem>
                        <MenuItem value={2}>{t('forms.dsForm.oneToFiveYears')}</MenuItem>
                        <MenuItem value={3}>{t('forms.dsForm.moreThanFiveYears')}</MenuItem>
                    </TextField>
                )}

                <FormControl component="fieldset" sx={{ display: 'block', marginTop: 2, marginBottom: 2, width: '100%' }}>
                    <FormLabel component="legend">{t("forms.dsForm.canceledVisa")}</FormLabel>
                    <RadioGroup row {...register('canceledVisa', {
                        onChange: (event) => setCanceledVisa(event.target.value === 'true')
                    })} defaultValue={false}>
                        <FormControlLabel value={true} control={<Radio />} label={t('forms.yes')} />
                        <FormControlLabel value={false} control={<Radio />} label={t('forms.no')} />
                    </RadioGroup>
                </FormControl>

                {canceledVisa && (
                    <TextField
                        id="whenWasVisaCanceled"
                        label={t('forms.dsForm.whenWasVisaCanceled')}
                        defaultValue={dsForm?.whenWasVisaCanceled}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        type="date"
                        size="small"
                        {...register('whenWasVisaCanceled', { 
                            required: canceledVisa ? t('forms.dsForm.whenWasVisaCanceledRequired') : false
                        })}
                        error={errors!.whenWasVisaCanceled ? true : false}
                        helperText={errors.whenWasVisaCanceled?.message}
                        sx={{
                            width: { xs: '100%', sm: '48%' },
                            marginTop: 2,
                            marginBottom: 2
                        }}
                    />
                )}

                <FormControl component="fieldset" sx={{ display: 'block', marginTop: 2, marginBottom: 2, width: '100%' }}>
                    <FormLabel component="legend">{t("forms.dsForm.citizenRequest")}</FormLabel>
                    <RadioGroup row {...register('citizenRequest')} defaultValue={false}>
                        <FormControlLabel value={true} control={<Radio />} label={t('forms.yes')} />
                        <FormControlLabel value={false} control={<Radio />} label={t('forms.no')} />
                    </RadioGroup>
                </FormControl>

                <FormControl component="fieldset" sx={{ display: 'block', marginTop: 2, marginBottom: 2, width: '100%' }}>
                    <FormLabel component="legend">{t("forms.dsForm.currentlyWithVisaProcess")}</FormLabel>
                    <RadioGroup row {...register('currentlyWithVisaProcess', {
                        onChange: (event) => setCurrentlyWithVisaProcess(event.target.value === 'true')
                    })} defaultValue={false}>
                        <FormControlLabel value={true} control={<Radio />} label={t('forms.yes')} />
                        <FormControlLabel value={false} control={<Radio />} label={t('forms.no')} />
                    </RadioGroup>
                </FormControl>

                {currentlyWithVisaProcess && (
                    <TextField
                        id="whatKindOfVisaIsProcessing"
                        label={t('forms.dsForm.whatKindOfVisaIsProcessing')}
                        variant="outlined"
                        size="small"
                        defaultValue={dsForm?.whatKindOfVisaIsProcessing}
                        select
                        {...register('whatKindOfVisaIsProcessing', { 
                            required: currentlyWithVisaProcess ? t('forms.dsForm.whatKindOfVisaIsProcessingRequired') : false
                        })}
                        error={errors!.whatKindOfVisaIsProcessing ? true : false}
                        helperText={errors.whatKindOfVisaIsProcessing?.message}
                        sx={{
                            width: { xs: '100%', sm: '48%' },
                            marginTop: 2,
                            marginBottom: 2
                        }}>
                        {visaTypes.map(visa => (
                            <MenuItem key={visa.value} value={t(visa.label)}>
                                {t(visa.label)}
                            </MenuItem>
                        ))}
                    </TextField>
                )}

                <FormControl component="fieldset" sx={{ display: 'block', marginTop: 2, marginBottom: 2, width: '100%' }}>
                    <FormLabel component="legend">{t("forms.dsForm.outOfCountryLastFiveYears")}</FormLabel>
                    <RadioGroup row {...register('outOfCountryLastFiveYears', {
                        onChange: (event) => setOutOfTheCountryLastFiveYears(event.target.value === 'true')
                    })} defaultValue={false}>
                        <FormControlLabel value={true} control={<Radio />} label={t('forms.yes')} />
                        <FormControlLabel value={false} control={<Radio />} label={t('forms.no')} />
                    </RadioGroup>
                </FormControl>

                {outOfTheCountryLastFiveYears &&
                    <TextField
                        {...register('visitedCountries', 
                            {
                                required: { value: outOfTheCountryLastFiveYears, message: "The field is required" }
                            }
                        )}
                        error={errors?.visitedCountries ? true : false}
                        helperText={ errors.visitedCountries?.message}
                        label={t("forms.dsForm.visitedCountries")}
                        variant="outlined"
                        fullWidth
                        margin="normal"
                    />
                }

                <FormControl component="fieldset" sx={{ display: 'block', marginTop: 2, marginBottom: 2, width: '100%' }}>
                    <FormLabel component="legend">{t("forms.dsForm.haveBeenOut")}</FormLabel>
                    <RadioGroup row {...register('haveBeenOut')} defaultValue={false}>
                        <FormControlLabel value={true} control={<Radio />} label={t('forms.yes')} />
                        <FormControlLabel value={false} control={<Radio />} label={t('forms.no')} />
                    </RadioGroup>
                </FormControl>

                <FormControl component="fieldset" sx={{ display: 'block', marginTop: 2, marginBottom: 2, width: '100%' }}>
                    <FormLabel component="legend">{t("forms.dsForm.triedEnterIllegallyToUs")}</FormLabel>
                    <RadioGroup row {...register('triedEnterIllegallyToUs')} defaultValue={false}>
                        <FormControlLabel value={true} control={<Radio />} label={t('forms.yes')} />
                        <FormControlLabel value={false} control={<Radio />} label={t('forms.no')} />
                    </RadioGroup>
                </FormControl>


                <Box sx={{ paddingTop: 10, paddingRight: 10 }} textAlign={'end'}>
                    <Button sx={{ margin: 2 }} variant="contained" color="info" onClick={handleBack} type="button" title="Back">Back</Button>
                    <Button variant="contained" type="submit" title="Send Request">Send</Button>
                </Box>
            </form>
        </Box>
    );
}